import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Mic,
  Send,
  Image as ImageIcon,
  Video,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Link2,
  X,
} from "lucide-react";
import { seededBars } from "../lib/waveform";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";
import { VoicePresetPicker, voicePresetHintText, voicePresetLabel } from "./VoicePresetPicker";
import { PermissionRecoveryDialog } from "./PermissionRecoveryDialog";
import { ExternalVideoPreview } from "./ExternalVideoPreview";
import { MAX_POST_VIDEO_BYTES, publishVoicePost, uploadPostMedia, validateExternalVideoUrl } from "../lib/postsApi";

const LARGE_POST_VIDEO_BYTES = 50 * 1024 * 1024;

function fmt(seconds) {
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function formatRemainingTime(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return `circa ${Math.max(1, Math.ceil(seconds))} secondi`;
  const minutes = Math.ceil(seconds / 60);
  return `circa ${minutes} ${minutes === 1 ? "minuto" : "minuti"}`;
}

function ComposerMic({ recording, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`mic-btn ${recording ? "!bg-ink animate-pulse" : ""}`}
      data-testid="composer-mic"
      aria-label={recording ? "Ferma registrazione" : "Avvia registrazione"}
    >
      <Mic size={20} />
    </button>
  );
}

function ComposerWaveform({ recording, seconds, maxSeconds, levels }) {
  const staticBars = useMemo(() => seededBars("composer-live", 46), []);
  const mmss = fmt(seconds);
  const nearLimit = seconds >= maxSeconds - 10 && recording;
  const bars = recording && levels.length > 0
    ? levels.map((l) => Math.max(3, Math.round(l * 26)))
    : staticBars.map((h, i) => (recording && i < (seconds * 6) % staticBars.length ? h : 0));
  return (
    <div className="flex-1 flex items-center gap-3 min-w-0 basis-40">
      <div className="waveform flex-1" style={{ height: 28 }}>
        {bars.map((h, i) => (
          <span key={`live-${i}`} className={h > 0 ? "on" : ""} style={{ height: `${Math.max(2, h)}px` }} />
        ))}
      </div>
      <span
        className={`text-xs tabular-nums shrink-0 ${nearLimit ? "text-primary font-semibold" : "text-muted"}`}
        data-testid="composer-timer"
      >
        {mmss}
      </span>
    </div>
  );
}

function AttachmentPreviews({ attachments, onRemove }) {
  if (attachments.length === 0) return null;
  return (
    <div
      className="mb-2 grid grid-cols-3 gap-2"
      data-testid="attachment-previews"
    >
      {attachments.map((item, index) => (
        <div key={item.id} className="relative aspect-video rounded-xl overflow-hidden border border-edge bg-cream">
          {item.kind === "image" ? (
            <img src={item.preview} alt={`Anteprima immagine ${index + 1}`} className="w-full h-full object-cover" />
          ) : item.externalUrl ? (
            <ExternalVideoPreview url={item.externalUrl} provider={item.provider} interactive={false} />
          ) : item.preview ? (
            <video src={item.preview} className="w-full h-full object-cover" muted controls />
          ) : (
            <div className="h-full p-2 flex items-center justify-center text-[11px] text-center break-all">
              <Link2 size={14} className="mr-1 shrink-0" />Video
            </div>
          )}
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="absolute right-1 top-1 w-6 h-6 rounded-full bg-ink text-white flex items-center justify-center"
            aria-label="Rimuovi allegato"
          >
            <X size={13} />
          </button>
          <span className="absolute left-1 bottom-1 rounded bg-black/70 text-white text-[9px] px-1.5 py-0.5">
            {item.kind === "image" ? "Immagine" : "Video"}
          </span>
        </div>
      ))}
    </div>
  );
}

function ComposerAttachments({ attachments, onAddImages, onAddVideo, onAddVideoLink, disabled, compact = false }) {
  const imageInput = useRef(null);
  const videoInput = useRef(null);
  const imageCount = attachments.filter((item) => item.kind === "image").length;
  const hasVideo = attachments.some((item) => item.kind === "video");
  const slotsLeft = 3 - attachments.length;
  return (
    <div className={compact ? "composer-mobile-attachments" : "min-w-0 flex-1 sm:flex-none"}>
      <div className="composer-attachment-buttons flex items-center gap-1 border-l border-edge pl-2">
        <input ref={imageInput} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
          onChange={(event) => { onAddImages(Array.from(event.target.files || [])); event.target.value = ""; }} />
        <input ref={videoInput} type="file" accept="video/mp4,video/webm" className="hidden"
          onChange={(event) => { const file = event.target.files?.[0]; if (file) onAddVideo(file); event.target.value = ""; }} />
        <button type="button" className="w-9 h-9 rounded-full text-ink hover:bg-cream flex items-center justify-center relative disabled:opacity-40"
          title="Aggiungi immagini" data-testid="attach-image" onClick={() => imageInput.current?.click()}
          disabled={disabled || slotsLeft === 0 || imageCount >= 3}>
          <ImageIcon size={17} />
          {imageCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center">{imageCount}</span>}
        </button>
        <button type="button" className={`w-9 h-9 rounded-full flex items-center justify-center hover:bg-cream disabled:opacity-40 ${hasVideo ? "text-primary" : "text-ink"}`}
          title="Carica video dal dispositivo" data-testid="attach-video" onClick={() => videoInput.current?.click()}
          disabled={disabled || slotsLeft === 0 || hasVideo || imageCount > 2}>
          <Video size={17} />
        </button>
        <button type="button" className="w-9 h-9 rounded-full text-ink hover:bg-cream flex items-center justify-center disabled:opacity-40"
          title="Aggiungi video tramite link" data-testid="attach-video-link" onClick={onAddVideoLink}
          disabled={disabled || slotsLeft === 0 || hasVideo || imageCount > 2}>
          <Link2 size={17} />
        </button>
      </div>
      <p className="composer-attachment-count mt-1 text-[10px] text-muted whitespace-nowrap">
        {attachments.length}/3 allegati · massimo 1 video
      </p>
    </div>
  );
}

function RecordingPreview({ blobUrl, durationMs, onDiscard, preset }) {
  const seconds = Math.floor(durationMs / 1000);
  const labelByPreset = {
    off: "Anteprima",
    anonimo: "Anteprima · Anonimo",
    grave: "Anteprima · Voce Grave",
    acuto: "Anteprima · Voce Acuta",
  };
  return (
    <div
      className="mt-2 flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 bg-white border border-primary/30 rounded-2xl sm:rounded-full pl-2 pr-3 py-1.5"
      data-testid="composer-preview"
    >
      <span className="text-[10px] uppercase tracking-widest text-primary font-semibold shrink-0">
        {labelByPreset[preset] || "Anteprima"}
      </span>
      <audio src={blobUrl} controls className="flex-1 h-8 min-w-0" data-testid="composer-preview-audio" />
      <span className="text-xs text-muted tabular-nums shrink-0">{fmt(seconds)}</span>
      <button
        className="w-7 h-7 rounded-full text-muted hover:text-primary flex items-center justify-center"
        onClick={onDiscard}
        title="Scarta"
        data-testid="composer-discard"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function composerStatusText({ recording, processing, hasRecording }) {
  if (recording) return "Sto registrando…";
  if (processing) return "Elaborazione voce…";
  if (hasRecording) return "Registrazione pronta";
  return "Registra il tuo messaggio";
}

export function ComposerBar({ topicSlug, onPublished }) {
  const [attachments, setAttachments] = useState([]);
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);
  const [publishedId, setPublishedId] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [videoUpload, setVideoUpload] = useState(null);
  const rec = useVoiceRecorder();

  useEffect(() => {
    if (!publishedId) return undefined;
    const timeoutId = window.setTimeout(() => setPublishedId(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [publishedId]);

  const toggleRecording = useCallback(() => {
    setPublishedId(null);
    setSendError(null);
    if (rec.recording) rec.stop();
    else void rec.start();
  }, [rec]);

  const changePreset = useCallback(
    (p) => {
      if (rec.recording) return;
      rec.setPreset(p);
    },
    [rec],
  );

  const addImages = useCallback((files) => {
    setSendError(null);
    setAttachments((current) => {
      const hasVideo = current.some((item) => item.kind === "video");
      const maxImages = hasVideo ? 2 : 3;
      const imageCount = current.filter((item) => item.kind === "image").length;
      const available = Math.min(3 - current.length, maxImages - imageCount);
      const accepted = files
        .filter((file) => ["image/jpeg", "image/png", "image/webp"].includes(file.type))
        .slice(0, Math.max(0, available))
        .map((file) => ({ id: crypto.randomUUID(), kind: "image", file, preview: URL.createObjectURL(file) }));
      return [...current, ...accepted];
    });
  }, []);
  const addVideo = useCallback((file) => {
    setSendError(null);
    setVideoUpload(null);
    if (!file || file.size <= 0) {
      setSendError("Il video selezionato è vuoto o non leggibile.");
      return;
    }
    if (file.size > MAX_POST_VIDEO_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      setSendError(`Video troppo grande: ${sizeMb} MB. Il limite massimo è 80 MB.`);
      return;
    }
    if (!["video/mp4", "video/webm"].includes(file.type)) {
      setSendError("Formato video non supportato. Usa un file MP4 o WebM.");
      return;
    }
    if (file.size > LARGE_POST_VIDEO_BYTES) {
      setVideoUpload({
        phase: "selected",
        sizeMb: (file.size / (1024 * 1024)).toFixed(1),
        percent: 0,
        etaSeconds: null,
      });
    }
    setAttachments((current) => {
      if (current.length >= 3 || current.some((item) => item.kind === "video") || current.filter((item) => item.kind === "image").length > 2) return current;
      return [...current, { id: crypto.randomUUID(), kind: "video", file, preview: URL.createObjectURL(file) }];
    });
  }, []);
  const addVideoLink = useCallback(() => {
    const value = window.prompt("Incolla il link del video da una piattaforma attendibile:");
    if (!value?.trim()) return;
    setAttachments((current) => {
      if (current.length >= 3 || current.some((item) => item.kind === "video") || current.filter((item) => item.kind === "image").length > 2) return current;
      return [...current, { id: crypto.randomUUID(), kind: "video", externalUrl: value.trim(), preview: "" }];
    });
  }, []);
  const removeAttachment = useCallback((id) => {
    setAttachments((current) => {
      const removed = current.find((item) => item.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      if (removed?.kind === "video") setVideoUpload(null);
      return current.filter((item) => item.id !== id);
    });
  }, []);

  const send = useCallback(async () => {
    if (!rec.blobUrl || rec.recording || sending || !topicSlug) return;
    setSending(true);
    setSendError(null);
    try {
      const blobResp = await fetch(rec.blobUrl);
      const blob = await blobResp.blob();
      const uploadedMedia = [];
      for (const item of attachments) {
        if (item.file && item.kind === "video" && item.file.size > LARGE_POST_VIDEO_BYTES) {
          const startedAt = Date.now();
          uploadedMedia.push(await uploadPostMedia(item.file, {
            onUploadProgress: (event) => {
              const loaded = Number(event.loaded) || 0;
              const total = Number(event.total) || item.file.size;
              const elapsedSeconds = Math.max(0.1, (Date.now() - startedAt) / 1000);
              const bytesPerSecond = loaded / elapsedSeconds;
              const etaSeconds = loaded < total && bytesPerSecond > 0
                ? (total - loaded) / bytesPerSecond
                : 0;
              setVideoUpload({
                phase: "uploading",
                sizeMb: (item.file.size / (1024 * 1024)).toFixed(1),
                percent: Math.max(0, Math.min(100, Math.round((loaded / total) * 100))),
                etaSeconds: elapsedSeconds >= 2 ? etaSeconds : null,
              });
            },
          }));
          setVideoUpload((current) => ({
            ...current,
            phase: "processing",
            percent: 100,
            etaSeconds: 0,
          }));
        } else if (item.file) {
          uploadedMedia.push(await uploadPostMedia(item.file));
        } else if (item.externalUrl) {
          uploadedMedia.push(await validateExternalVideoUrl(item.externalUrl));
        }
      }
      const post = await publishVoicePost({
        topicSlug,
        durationMs: rec.durationMs,
        blob,
        caption,
        mediaAttachments: uploadedMedia,
        voiceModified: rec.preset !== "off",
      });
      setPublishedId(post.id);
      rec.reset();
      attachments.forEach((item) => { if (item.preview) URL.revokeObjectURL(item.preview); });
      setAttachments([]);
      setCaption("");
      setVideoUpload(null);
      if (typeof onPublished === "function") onPublished(post);
    } catch (err) {
      const detail =
        err?.response?.data?.message || err?.message || "Errore sconosciuto";
      setVideoUpload(null);
      setSendError(`Invio fallito: ${detail}`);
    } finally {
      setSending(false);
    }
  }, [rec, sending, topicSlug, onPublished, caption, attachments]);

  const canSend = !!rec.blobUrl && !rec.recording && !sending && !rec.processing;
  const presetLabel = voicePresetLabel(rec.preset);

  return (
    <div className="border-t border-edge bg-cream/70 p-2 sm:p-4" data-testid="composer-bar">
      {(rec.error || sendError) && (
        <div className="mb-2 flex items-start gap-2 text-sm font-semibold text-white bg-primary border border-primary rounded-xl px-3 py-2 shadow-sm" data-testid={sendError ? "composer-send-error" : "composer-error"} role="alert">
          <AlertCircle size={17} className="mt-0.5 shrink-0" />
          <span>{sendError || rec.error}</span>
        </div>
      )}
      {videoUpload && (
        <div
          className="mb-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
          data-testid="composer-video-upload-status"
          aria-live="polite"
        >
          <div className="flex items-start gap-2">
            {videoUpload.phase === "uploading" || videoUpload.phase === "processing" ? (
              <Loader2 size={17} className="mt-0.5 shrink-0 animate-spin" />
            ) : (
              <AlertCircle size={17} className="mt-0.5 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                {videoUpload.phase === "selected"
                  ? `Video pesante (${videoUpload.sizeMb} MB)`
                  : videoUpload.phase === "processing"
                    ? "Video caricato, verifica in corso…"
                    : `Caricamento video: ${videoUpload.percent}%`}
              </p>
              <p className="text-xs">
                {videoUpload.phase === "selected"
                  ? "Il caricamento potrebbe richiedere alcuni minuti. Non chiudere l’app durante l’invio."
                  : videoUpload.phase === "processing"
                    ? "Attendi il completamento della revisione."
                    : formatRemainingTime(videoUpload.etaSeconds)
                      ? `Tempo rimanente: ${formatRemainingTime(videoUpload.etaSeconds)}`
                      : "Calcolo del tempo rimanente…"}
              </p>
              {videoUpload.phase === "uploading" && (
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-amber-200">
                  <div
                    className="h-full bg-primary transition-[width] duration-200"
                    style={{ width: `${videoUpload.percent}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <AttachmentPreviews attachments={attachments} onRemove={removeAttachment} />
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 bg-white rounded-2xl sm:rounded-full border border-edge shadow-sm px-2 sm:px-3 py-2 overflow-hidden">
        <ComposerMic recording={rec.recording} onToggle={toggleRecording} />
        <div className="hidden xl:block min-w-0 max-w-[220px]">
          <div className="font-semibold text-sm text-ink leading-tight truncate">
            {composerStatusText({
              recording: rec.recording,
              processing: rec.processing,
              hasRecording: !!rec.blobUrl,
            })}
          </div>
          <div className="text-[11px] text-muted truncate">
            Max {Math.floor(rec.maxSeconds / 60)} min · Voce: {presetLabel}
          </div>
        </div>
        <ComposerWaveform
          recording={rec.recording}
          seconds={rec.seconds}
          maxSeconds={rec.maxSeconds}
          levels={rec.levels}
        />
        <div className="hidden sm:contents">
          <VoicePresetPicker
            value={rec.preset}
            disabled={rec.recording || rec.processing || !!rec.blobUrl}
            onChange={changePreset}
            testId="composer-voice-preset"
          />
          <div
            className="w-auto grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 min-w-0"
            data-testid="composer-actions-row"
          >
            <ComposerAttachments
              attachments={attachments}
              onAddImages={addImages}
              onAddVideo={addVideo}
              onAddVideoLink={addVideoLink}
              disabled={rec.recording || rec.processing || sending}
            />
            <button
              className="brutal-send shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="composer-send"
              disabled={!canSend}
              onClick={send}
              title={canSend ? "Invia" : "Registra prima un messaggio"}
            >
              <Send size={14} />
              <span>{sending ? "Invio…" : "Invia"}</span>
            </button>
          </div>
        </div>

        <div className="composer-mobile-toolbar sm:hidden" data-testid="composer-mobile-toolbar">
          <ComposerAttachments
            attachments={attachments}
            onAddImages={addImages}
            onAddVideo={addVideo}
            onAddVideoLink={addVideoLink}
            disabled={rec.recording || rec.processing || sending}
            compact
          />
          <div className="composer-mobile-voice-send">
            <VoicePresetPicker
              value={rec.preset}
              disabled={rec.recording || rec.processing || !!rec.blobUrl}
              onChange={changePreset}
              testId="composer-voice-preset-mobile"
              compact
            />
            <button
              className="composer-mobile-send disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="composer-send-mobile"
              disabled={!canSend}
              onClick={send}
              title={canSend ? "Invia" : "Registra prima un messaggio"}
              aria-label={sending ? "Invio in corso" : "Invia"}
            >
              <Send size={15} />
              <span>{sending ? "Invio…" : "Invia"}</span>
            </button>
          </div>
        </div>
      </div>

      {rec.processing && (
        <div
          className="mt-2 flex items-center gap-2 text-[11px] text-ink/70 bg-ink/5 border border-ink/10 rounded-full px-3 py-1.5"
          data-testid="composer-processing"
        >
          <Loader2 size={13} className="animate-spin shrink-0" />
          Sto applicando l'effetto {presetLabel.toLowerCase()} alla registrazione…
        </div>
      )}

      {rec.preset !== "off" && !rec.recording && !rec.blobUrl && !rec.processing && (
        <div
          className="mt-2 flex items-center gap-2 text-[11px] text-ink/70 bg-ink/5 border border-ink/10 rounded-full px-3 py-1.5"
          data-testid="composer-preset-hint"
        >
          Preset attivo: <strong>{presetLabel}</strong>{" · "}
          {voicePresetHintText(rec.preset)}
        </div>
      )}

      {publishedId && (
        <div
          className="mt-2 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5"
          data-testid="composer-published"
        >
          <CheckCircle2 size={13} /> Post pubblicato ·{" "}
          <span className="font-mono text-[10px]">{publishedId.slice(0, 8)}</span>
        </div>
      )}
      <PermissionRecoveryDialog
        issue={rec.permissionIssue}
        onClose={rec.clearPermissionIssue}
        onRetry={rec.start}
      />
      {rec.blobUrl && !rec.recording && !rec.processing && (
        <div className="mt-2 space-y-2">
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 240))}
            placeholder="Aggiungi una didascalia (opzionale, max 240)"
            className="w-full px-4 py-2 rounded-full border border-edge bg-white text-sm outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
            data-testid="composer-caption"
          />
          <RecordingPreview
            blobUrl={rec.blobUrl}
            durationMs={rec.durationMs}
            onDiscard={rec.reset}
            preset={rec.preset}
          />
        </div>
      )}
    </div>
  );
}
